// apps/client/src/assets/builders/rock.ts (trees and rocks, 2026-10-16) — original soft pebbles in place of the kit rocks: smooth,
// gently lumpy, pale warm stone (DESIGN.md rocks: #D9D0BE), lighter on top and darker where they meet the grass.
// Params (content:assets/world.ts): { seed, width, height, count (1 to 3 pebbles), stack? (the second rests on the first), stone }.
import { rngFrom, type Rng } from '@whiskerton/shared';
import * as THREE from 'three';
import { icosphere, mergeParts, NATURE_MATERIAL, paintVertices, smoothstep } from '../organic';
import type { CodeBuilder } from '../service';

const num = (v: unknown, d: number) => (typeof v === 'number' && Number.isFinite(v) ? v : d);

/** One pebble `w` wide, `h` tall and `d` deep, its base on y = 0 at (x, z) (`lift` raises it onto another), smooth and a little lumpy. */
function pebble(rng: Rng, x: number, z: number, w: number, h: number, d: number, yaw: number, lift = 0): THREE.BufferGeometry {
  const g = icosphere(1), pos = g.getAttribute('position') as THREE.BufferAttribute, u = new THREE.Vector3();
  const f = [1 + rng.next() * 2, 1 + rng.next() * 2, 1 + rng.next() * 2], ph = [rng.next() * 6.28, rng.next() * 6.28, rng.next() * 6.28];
  const c = Math.cos(yaw), s = Math.sin(yaw);
  for (let i = 0; i < pos.count; i++) {
    u.fromBufferAttribute(pos, i);
    // low lumps over the surface; the underside is flatter, so the pebble sits rather than balances
    const lump = 1 + 0.07 * Math.sin(f[0]! * u.x + ph[0]!) * Math.sin(f[1]! * u.z + ph[1]!) + 0.05 * Math.sin(f[2]! * u.y + ph[2]!);
    const lx = u.x * lump * (w / 2), lz = u.z * lump * (d / 2);
    const ly = (u.y < 0 ? u.y * 0.45 : u.y) * lump * h * 0.72;
    pos.setXYZ(i, x + lx * c - lz * s, lift + h * 0.3 + ly, z + lx * s + lz * c);
  }
  g.computeVertexNormals();
  return g;
}

export const builder: CodeBuilder = {
  id: 'rock',
  build(p) {
    const rng = rngFrom('rock', num(p.seed, 1));
    const W = num(p.width, 0.8), H = num(p.height, 0.4), count = Math.min(3, Math.max(1, Math.round(num(p.count, 1))));
    const stone = new THREE.Color(typeof p.stone === 'string' && /^#[0-9a-f]{6}$/i.test(p.stone) ? p.stone : '#D9D0BE');
    const parts: THREE.BufferGeometry[] = [];
    if (count === 1) parts.push(pebble(rng, 0, 0, W, H, W * 0.86, rng.next() * 3));
    else if (p.stack === true) {                                   // a flat pebble with a smaller one resting on it
      parts.push(pebble(rng, 0, 0, W, H * 0.5, W * 0.84, rng.next() * 3));
      parts.push(pebble(rng, W * 0.08, -W * 0.04, W * 0.55, H * 0.55, W * 0.48, rng.next() * 3, H * 0.4));
    } else if (count === 2) {                                      // a pebble with a little one at its side
      parts.push(pebble(rng, -W * 0.12, 0, W * 0.72, H, W * 0.66, rng.next() * 3));
      parts.push(pebble(rng, W * 0.3, W * 0.12, W * 0.38, H * 0.5, W * 0.34, rng.next() * 3));
    } else {                                                       // a cluster: one big, two small round its front
      parts.push(pebble(rng, -W * 0.1, -W * 0.06, W * 0.6, H, W * 0.56, rng.next() * 3));
      parts.push(pebble(rng, W * 0.29, W * 0.08, W * 0.4, H * 0.62, W * 0.36, rng.next() * 3));
      parts.push(pebble(rng, -W * 0.3, W * 0.2, W * 0.3, H * 0.42, W * 0.27, rng.next() * 3));
    }
    // Each pebble shades by which way it faces (tops lighter, undersides darker), and only where it meets the grass is it darker
    // still: a pebble resting on another stays light all round.
    const light = stone.clone().lerp(new THREE.Color('#FFF8EA'), 0.22), dark = stone.clone().multiplyScalar(0.55).lerp(new THREE.Color('#6F7468'), 0.25);
    const mesh = new THREE.Mesh(mergeParts(parts.map((g) => paintVertices(g, (q, n, out) => {
      const speck = 0.96 + 0.08 * (Math.sin(q.x * 37.1 + q.z * 17.3) * Math.sin(q.y * 41.7 + q.x * 9.1) * 0.5 + 0.5);
      out.copy(dark).lerp(stone, smoothstep(-0.7, 0.25, n.y)).lerp(light, smoothstep(0.4, 0.95, n.y) * 0.6)
        .lerp(dark, (1 - smoothstep(0, Math.min(0.12, H * 0.35), q.y)) * 0.6);
      out.multiplyScalar(speck);
    }))), NATURE_MATERIAL);
    mesh.name = 'stone';
    mesh.userData.tintJitter = 0.05;                               // each instance a shade lighter or darker (world/instancer.ts)
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    const g = new THREE.Group();
    g.name = 'rock';
    g.add(mesh);
    return g;
  },
};
